import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/concierge`;
const KEY = "wr_ta_session";
const Ctx = createContext(null);

const cid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const loadCreds = () => { try { return JSON.parse(window.localStorage.getItem(KEY)) || null; } catch (_) { return null; } };
const saveCreds = (c) => { try { c ? window.localStorage.setItem(KEY, JSON.stringify(c)) : window.localStorage.removeItem(KEY); } catch (_) {} };
const headers = (c) => ({ "Content-Type": "application/json", "X-Session-Id": c.session_id, "X-Session-Token": c.token });

async function createSession() {
  const res = await fetch(`${API}/session`, { method: "POST" });
  if (!res.ok) throw new Error("session");
  const c = await res.json();
  saveCreds(c);
  return c;
}

async function readSSE(res, onEvent) {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() || "";
    for (const p of parts) {
      const line = p.trim();
      if (!line.startsWith("data:")) continue;
      try { onEvent(JSON.parse(line.slice(5).trim())); } catch (_) {}
    }
  }
}

export function ConciergeProvider({ children }) {
  const [messages, setMessages] = useState([]);
  const [profile, setProfile] = useState(null);
  const [costs, setCosts] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const creds = useRef(loadCreds());
  const queue = useRef([]);      // client messages not yet sent
  const busy = useRef(false);
  const failed = useRef([]);     // batch to retry

  const applyState = (s) => {
    if (s.profile) setProfile(s.profile);
    if ("costs" in s) setCosts(s.costs);
  };

  useEffect(() => {
    const c = creds.current;
    if (!c) return;
    fetch(`${API}/state`, { headers: headers(c) })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((s) => {
        applyState(s);
        setMessages(s.messages.map((m) => ({ id: m.id, client_id: m.client_id, role: m.role, content: m.content, costs: m.costs || null })));
        const last = s.messages[s.messages.length - 1];
        if (last && last.role === "user") {
          failed.current = s.messages.filter((m, i) => i > s.messages.map((x) => x.role).lastIndexOf("assistant")).map((m) => ({ client_id: m.client_id, text: m.content }));
          setError({ code: "unanswered", retryable: true });
        }
      })
      .catch((st) => { if (st === 401) { saveCreds(null); creds.current = null; } });
  }, []);

  const flush = useCallback(async (locale, batchOverride) => {
    if (busy.current) return;
    const batch = batchOverride || queue.current.splice(0, queue.current.length);
    if (!batch.length) return;
    busy.current = true;
    setStreaming(true);
    setError(null);
    const aid = cid();
    setMessages((prev) => [...prev, { id: aid, role: "assistant", content: "", pending: true }]);
    const patchA = (fn) => setMessages((prev) => prev.map((m) => (m.id === aid ? fn(m) : m)));
    let ok = false;
    try {
      if (!creds.current) creds.current = await createSession();
      let res = await fetch(`${API}/chat`, { method: "POST", headers: headers(creds.current), body: JSON.stringify({ messages: batch, locale }) });
      if (res.status === 401) throw Object.assign(new Error("expired"), { code: "session_expired" });
      if (!res.ok || !res.body) throw Object.assign(new Error("http"), { code: "reply_failed" });
      let errEv = null;
      await readSSE(res, (ev) => {
        if (ev.type === "delta") patchA((m) => ({ ...m, content: m.content + ev.content }));
        else if (ev.type === "profile") applyState(ev);
        else if (ev.type === "done") { ok = true; applyState(ev); patchA((m) => ({ ...m, id: ev.message_id, pending: false, costs: ev.message_costs || null })); }
        else if (ev.type === "error") errEv = ev;
      });
      if (!ok) throw Object.assign(new Error("stream"), { code: errEv?.code || "reply_failed", retryable: errEv ? errEv.retryable : true });
      failed.current = [];
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== aid));
      failed.current = batch;
      if (e.code === "session_expired") { saveCreds(null); creds.current = null; }
      setError({ code: e.code || "network", retryable: e.retryable !== false && e.code !== "session_expired" });
    } finally {
      busy.current = false;
      setStreaming(false);
    }
    if (ok && queue.current.length) flush(locale);
  }, []);

  const send = useCallback((text, locale) => {
    const t = (text || "").trim();
    if (!t) return;
    const m = { client_id: cid(), text: t };
    setMessages((prev) => [...prev, { id: m.client_id, client_id: m.client_id, role: "user", content: t }]);
    if (failed.current.length && !busy.current) {
      const batch = [...failed.current, m];
      failed.current = [];
      flush(locale, batch);
      return;
    }
    queue.current.push(m);
    flush(locale);
  }, [flush]);

  const retry = useCallback((locale) => {
    if (!failed.current.length) return;
    const batch = failed.current;
    failed.current = [];
    flush(locale, batch);
  }, [flush]);

  const editField = useCallback(async (field, value, action = "set") => {
    if (!creds.current) return false;
    const res = await fetch(`${API}/profile`, { method: "PATCH", headers: headers(creds.current), body: JSON.stringify({ field, value, action }) });
    if (!res.ok) return false;
    applyState(await res.json());
    return true;
  }, []);

  const getSummary = useCallback(async (locale) => {
    if (!creds.current) return null;
    const res = await fetch(`${API}/summary?locale=${locale === "ro" ? "ro" : "en"}`, { headers: headers(creds.current) });
    return res.ok ? (await res.json()).text : null;
  }, []);

  const clear = useCallback(async () => {
    const c = creds.current;
    const res = await fetch(`${API}/session/reset`, { method: "POST", headers: c ? headers(c) : {} });
    const fresh = res.ok ? await res.json() : null;
    saveCreds(fresh);
    creds.current = fresh;
    queue.current = []; failed.current = [];
    setMessages([]); setProfile(null); setCosts(null); setError(null);
  }, []);

  return (
    <Ctx.Provider value={{ messages, profile, costs, streaming, error, send, retry, editField, clear, getSummary }}>
      {children}
    </Ctx.Provider>
  );
}

export const useConcierge = () => useContext(Ctx);
